import {
  CoreInputModelUtil,
  getNewErrors,
  IColor,
  InputErrorModel,
} from "@mindmemobile/mm-shared-global";
import { autorun } from "mobx";
import {
  CheckOutButtonStyle,
  CheckoutColorType,
  CheckOutDefaultSegmentType,
  CheckOutHelpType,
  CheckOutPayButtonCaption,
  ImageFormatTypes,
  ImagePlacementTypes,
  PaymentMethodTypes,
} from "../enums";
import {
  basicColorPickerElements,
  bgColorPickerElements,
  borderColorPickerElements,
  donationBgColors,
  donationBorderColors,
  donationFontColors,
  donationMainColors,
  mainColorPickerElements,
  progressColorPickerElements,
} from "./CheckoutStaticData";
import {
  IAPIBasicColors,
  IAPICheckoutBrandingModel,
  IAPICheckoutOptionsModel,
  IAPICustomColors,
  IAPILinkedMediaModel,
  IAPIListInterestTagModel,
  IAPIMediaPlacementModel,
} from "../interfaces";
import { CountryWithStateModel } from "./CountryModels";
import { ColorModel } from "./ProductModel";
import { MediaModel } from "./MediaModel";
import { ListInterestTagModel } from "./ListInterestTagModel";

export class CheckoutOptionsPageModel {
  selectedPaymentMethods: string[];
  selectedList: ListInterestTagModel[] = [];
  selectedTag: ListInterestTagModel[] = [];
  isAddPayeePopup: boolean;
  brandingData: CheckoutBrandingModel;
  errors: InputErrorModel<CheckoutOptionsPageModel>[];
  constructor(data?: Partial<CheckoutOptionsPageModel>) {
    const defaultPaymentMethod = Object.keys(PaymentMethodTypes).find(
      (f) =>
        PaymentMethodTypes[f as keyof typeof PaymentMethodTypes] ===
        PaymentMethodTypes.CreditDebitCard
    );
    this.selectedPaymentMethods = data?.selectedPaymentMethods || [];

    if (
      !this.selectedPaymentMethods ||
      this.selectedPaymentMethods.length === 0
    ) {
      if (defaultPaymentMethod) {
        this.selectedPaymentMethods = [defaultPaymentMethod];
      }
    }
    this.selectedList =
      (data &&
        data.selectedList?.map((e) => {
          return new ListInterestTagModel(e);
        })) ||
      [];
    this.selectedTag =
      (data &&
        data.selectedTag?.map((e) => {
          return new ListInterestTagModel(e);
        })) ||
      [];
    this.isAddPayeePopup = data?.isAddPayeePopup || false;
    this.brandingData = data?.brandingData || new CheckoutBrandingModel();
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<CheckoutOptionsPageModel>(e);
        })) ||
      [];

    autorun(() => {
      this.validate(true);
    });
  }

  hasError(field: keyof CheckoutOptionsPageModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<CheckoutOptionsPageModel>[] = [];
    const isValidBrandingContent = this.brandingData.validate(isAutoRun);
    this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;
    return this.errors.length === 0 && isValidBrandingContent;
  }

  createAPIModel(isDonation: boolean = false): IAPICheckoutOptionsModel {
    return {
      ...this.brandingData.createAPIModel(isDonation),
      availablePaymentMethods: this.selectedPaymentMethods,
    };
  }

  static deserializeAPIModel(
    data: IAPICheckoutOptionsModel,
    lists?: IAPIListInterestTagModel[],
    tags?: IAPIListInterestTagModel[]
  ) {
    if (!data) return new CheckoutOptionsPageModel();
    const mappedData: Partial<CheckoutOptionsPageModel> = {
      brandingData: CheckoutBrandingModel.deserializeAPIModel(data),
      selectedPaymentMethods: data.availablePaymentMethods || [],
      selectedList:
        lists?.map((l) => {
          return ListInterestTagModel.deserialize(l);
        }) || [],
      selectedTag:
        tags?.map((l) => {
          return ListInterestTagModel.deserialize(l);
        }) || [],
    };
    return new CheckoutOptionsPageModel(mappedData);
  }

  static deserializeDonationData(
    data: IAPICheckoutOptionsModel,
    media: IAPILinkedMediaModel,
    mediaPlacement: IAPIMediaPlacementModel
  ) {
    if (!data) return new CheckoutOptionsPageModel();
    const mappedData: Partial<CheckoutOptionsPageModel> = {
      brandingData: CheckoutBrandingModel.deserializeDonationAPIModel(
        data,
        media,
        mediaPlacement
      ),
      selectedPaymentMethods: data.availablePaymentMethods || [],
    };
    return new CheckoutOptionsPageModel(mappedData);
  }
}

const defaultColor: IColor = {
  hex: "",
  isGradient: false,
  gradient: "",
  rgbValue: { r: "", g: "", b: "", a: "" },
  rgba: "",
};

const defaultColors = {
  primary: defaultColor,
  tabs: defaultColor,
  innerBackground: defaultColor,
  outerBackground: defaultColor,
  recommended: defaultColor,
  pageBackground: defaultColor,
  innerBorder: defaultColor,
  outerBorder: defaultColor,
  selectedBar: defaultColor,
  unSelectedBar: defaultColor,
};

export class CheckoutBrandingModel {
  showLogo: boolean;
  showHelp: boolean;
  sellerContact: keyof typeof CheckOutHelpType;
  helpEmail: string;
  phoneNumber: string;
  selectedColorType: keyof typeof CheckoutColorType;
  errors: InputErrorModel<CheckoutBrandingModel>[];
  countryDetails: CountryWithStateModel;
  customColors: CustomColors;
  basicColors: BasicColors;
  donationCheckout: DonationCheckoutModel;

  constructor(data?: Partial<CheckoutBrandingModel>) {
    this.showHelp = data?.showHelp || false;
    this.showLogo = data?.showLogo || false;
    this.sellerContact = data?.sellerContact || CheckOutHelpType.Phone;
    this.helpEmail = data?.helpEmail || "";
    this.phoneNumber = data?.phoneNumber || "";
    this.countryDetails = data?.countryDetails || new CountryWithStateModel();
    this.selectedColorType =
      data?.selectedColorType || CheckoutColorType.BasicColor;
    this.customColors = data?.customColors || new CustomColors();
    this.basicColors = data?.basicColors || new BasicColors();
    this.donationCheckout =
      data?.donationCheckout || new DonationCheckoutModel();
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<CheckoutBrandingModel>(e);
        })) ||
      [];

    autorun(() => {
      this.validate(true);
    });
  }

  hasError(field: keyof CheckoutBrandingModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  validateContact(newErrors: InputErrorModel<CheckoutBrandingModel>[]) {
    if (this.showHelp) {
      if (this.sellerContact === CheckOutHelpType.Phone) {
        CoreInputModelUtil.validatePhoneNumber(
          "phoneNumber",
          this.phoneNumber,
          newErrors
        );
      } else {
        CoreInputModelUtil.validateEmail(
          "helpEmail",
          this.helpEmail,
          newErrors
        );
      }
    }
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<CheckoutBrandingModel>[] = [];
    let isValidColors = true;
    this.validateContact(newErrors);
    if (this.selectedColorType === CheckoutColorType.CustomColor) {
      isValidColors = this.customColors.validate(isAutoRun);
    } else {
      isValidColors = this.basicColors.validate(isAutoRun);
    }
    this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;
    return this.errors.length === 0 && isValidColors;
  }

  createAPIModel(isDonation: boolean = false): IAPICheckoutBrandingModel {
    return {
      showLogo: this.showLogo,
      showHelp: this.showHelp,
      helpEmail: this.helpEmail,
      helpType: this.sellerContact,
      helpPhone:
        this.showHelp && this.sellerContact === CheckOutHelpType.Phone
          ? {
              phoneNumber: this.phoneNumber,
              countryCode: this.countryDetails.code,
              countryId: this.countryDetails.id,
            }
          : undefined,
      colorType: this.selectedColorType,
      customColors: isDonation
        ? this.donationCheckout.createColorsAPIModel()
        : this.customColors.createAPIModel(),
      basicColors: this.basicColors.createAPIModel(),
    };
  }

  static deserializeAPIModel(
    data: Partial<IAPICheckoutBrandingModel>
  ): CheckoutBrandingModel {
    if (!data) {
      return new CheckoutBrandingModel();
    }

    const mappedData: Partial<CheckoutBrandingModel> = {
      showHelp: data.showHelp,
      showLogo: data.showLogo,
      sellerContact: data.helpType as keyof typeof CheckOutHelpType,
      helpEmail: data.helpEmail,
      phoneNumber: data.helpPhone?.phoneNumber,
      countryDetails: new CountryWithStateModel({
        id: data.helpPhone?.countryId,
        code: data.helpPhone?.countryCode,
      }),
      selectedColorType: data.colorType,
      customColors: CustomColors.deserializeAPIModel(
        data.customColors || new CustomColors()
      ),
      basicColors: BasicColors.deserializeAPIModel(
        data.basicColors || new BasicColors()
      ),
    };
    return new CheckoutBrandingModel(mappedData);
  }

  static deserializeDonationAPIModel(
    data: Partial<IAPICheckoutBrandingModel>,
    media: IAPILinkedMediaModel,
    mediaPlacement: IAPIMediaPlacementModel
  ): CheckoutBrandingModel {
    if (!data) {
      return new CheckoutBrandingModel();
    }

    const mappedData: Partial<CheckoutBrandingModel> = {
      showHelp: data.showHelp,
      showLogo: data.showLogo,
      sellerContact: data.helpType as keyof typeof CheckOutHelpType,
      helpEmail: data.helpEmail,
      phoneNumber: data.helpPhone?.phoneNumber,
      countryDetails: new CountryWithStateModel({
        id: data.helpPhone?.countryId,
        code: data.helpPhone?.countryCode,
      }),
      selectedColorType: data.colorType,
      donationCheckout: DonationCheckoutModel.deserializeAPIModel(
        mediaPlacement,
        media,
        data.customColors as IAPICustomColors
      ),
    };
    return new CheckoutBrandingModel(mappedData);
  }
}

export class ColorPickerElement {
  color: ColorModel | null;
  name: string;
  showColorPicker: boolean;

  constructor(data?: Partial<ColorPickerElement>) {
    this.color = data?.color || defaultColor;
    this.name = data?.name || "";
    this.showColorPicker = data?.showColorPicker || false;
  }

  deserializeColor(): IColor {
    return this.color as IColor;
  }

  createAPIModel(): IColor {
    return this.color as IColor;
  }
}

export class CustomColors {
  buttonStyleType: keyof typeof CheckOutButtonStyle;
  buttonCustomCaption: string;
  payButtonType: keyof typeof CheckOutPayButtonCaption;
  defaultSegment: keyof typeof CheckOutDefaultSegmentType;
  colorPickers: { [key: string]: ColorPickerElement[] };
  errors: InputErrorModel<CustomColors>[];

  constructor(data?: Partial<CustomColors>) {
    this.colorPickers = {
      main: (data?.colorPickers?.main ?? mainColorPickerElements).map(
        (el) => new ColorPickerElement(el)
      ),
      border: (data?.colorPickers?.border ?? borderColorPickerElements).map(
        (el) => new ColorPickerElement(el)
      ),
      background: (data?.colorPickers?.background ?? bgColorPickerElements).map(
        (el) => new ColorPickerElement(el)
      ),
      progress: (
        data?.colorPickers?.progress ?? progressColorPickerElements
      ).map((el) => new ColorPickerElement(el)),
    };
    this.buttonStyleType =
      (data?.buttonStyleType as keyof typeof CheckOutButtonStyle) ||
      CheckOutButtonStyle.SquareRoundCorner;
    this.buttonCustomCaption = data?.buttonCustomCaption || "";
    this.payButtonType =
      (data?.payButtonType as keyof typeof CheckOutPayButtonCaption) ||
      CheckOutPayButtonCaption.Pay;
    this.defaultSegment =
      (data?.defaultSegment as keyof typeof CheckOutDefaultSegmentType) ||
      (Object.keys(
        CheckOutDefaultSegmentType
      )[0] as keyof typeof CheckOutDefaultSegmentType);
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<CustomColors>(e);
        })) ||
      [];
  }

  getCustomColors() {
    const findColor = (pickers: ColorPickerElement[], name: string) =>
      pickers.find((el) => el.name === name)?.createAPIModel() ?? defaultColor;

    return {
      primary: findColor(this.colorPickers.main, "Primary"),
      tabs: findColor(this.colorPickers.main, "Tabs"),
      recommended: findColor(this.colorPickers.main, "Recommended"),
      innerBackground: findColor(this.colorPickers.background, "Inner"),
      outerBackground: findColor(this.colorPickers.background, "Outer"),
      pageBackground: findColor(this.colorPickers.background, "Page"),
      innerBorder: findColor(this.colorPickers.border, "Inner"),
      outerBorder: findColor(this.colorPickers.border, "Outer"),
      selectedBar: findColor(this.colorPickers.progress, "Selected Bar"),
      unSelectedBar: findColor(this.colorPickers.progress, "Unselected Bar"),
    };
  }

  createAPIModel(): IAPICustomColors {
    return {
      ...this.getCustomColors(),
      notSelected: defaultColor,
      buttonStyleType: this.buttonStyleType,
      buttonCustomCaption: this.buttonCustomCaption,
      payButtonType: this.payButtonType,
      defaultSegment: this.defaultSegment,
      fontText: defaultColor,
      fontActiveButton: defaultColor,
      fontInactiveButton: defaultColor,
    };
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<CustomColors>[] = [];
    if (this.payButtonType === CheckOutPayButtonCaption.Custom) {
      CoreInputModelUtil.validateTextField(
        this.buttonCustomCaption,
        "buttonCustomCaption",
        newErrors
      );
    }
    CoreInputModelUtil.validateTextField(
      this.payButtonType,
      "payButtonType",
      newErrors
    );
    CoreInputModelUtil.validateTextField(
      this.defaultSegment,
      "defaultSegment",
      newErrors
    );
    this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;
    return this.errors.length === 0;
  }

  hasError(field: keyof CustomColors): boolean {
    return this.errors.some((e) => e.field === field);
  }

  static deserializeAPIModel(data: Partial<IAPICustomColors>): CustomColors {
    if (!data) {
      return new CustomColors();
    }
    const customColors = data ? { ...defaultColors, ...data } : defaultColors;

    const createColorPickers = (names: string[], colors: IColor[]) =>
      names.map(
        (name, i) => new ColorPickerElement({ name, color: colors[i] })
      );

    const mappedData: Partial<CustomColors> = {
      colorPickers: {
        main: createColorPickers(
          ["Primary", "Tabs", "Recommended"],
          [customColors.primary, customColors.tabs, customColors.recommended]
        ),
        background: createColorPickers(
          ["Inner", "Outer", "Page"],
          [
            customColors.innerBackground,
            customColors.outerBackground,
            customColors.pageBackground,
          ]
        ),
        border: createColorPickers(
          ["Inner", "Outer"],
          [customColors.innerBorder, customColors.outerBorder]
        ),
        progress: createColorPickers(
          ["Selected Bar", "Unselected Bar"],
          [customColors.selectedBar, customColors.unSelectedBar]
        ),
      },
      buttonCustomCaption: data.buttonCustomCaption,
      payButtonType:
        data.payButtonType as keyof typeof CheckOutPayButtonCaption,
      defaultSegment:
        data.defaultSegment as keyof typeof CheckOutDefaultSegmentType,
      buttonStyleType: data.buttonStyleType as keyof typeof CheckOutButtonStyle,
    };
    return new CustomColors(mappedData);
  }
}

export class DonationCheckoutModel {
  mainColors: ColorPickerElement[];
  backgroundColors: ColorPickerElement[];
  borderColors: ColorPickerElement[];
  fontColors: ColorPickerElement[];
  media: MediaModel[];
  imagePlacement: keyof typeof ImagePlacementTypes;
  imageFormat: keyof typeof ImageFormatTypes;
  imageHideOnMobile: boolean;
  placementSlider: number;

  constructor(data?: Partial<DonationCheckoutModel>) {
    this.mainColors = (data?.mainColors ?? donationMainColors).map(
      (el) => new ColorPickerElement(el)
    );
    this.backgroundColors = (data?.backgroundColors ?? donationBgColors).map(
      (el) => new ColorPickerElement(el)
    );
    this.borderColors = (data?.borderColors ?? donationBorderColors).map(
      (el) => new ColorPickerElement(el)
    );
    this.fontColors = (data?.fontColors ?? donationFontColors).map(
      (el) => new ColorPickerElement(el)
    );
    this.media = data?.media || [];
    this.imageFormat = data?.imageFormat || ImageFormatTypes.ScreenFit;
    this.imagePlacement = data?.imagePlacement || ImagePlacementTypes.Left;
    this.imageHideOnMobile = data?.imageHideOnMobile || false;
    this.placementSlider = data?.placementSlider || 50;
  }

  createAPIModel(): IAPIMediaPlacementModel {
    return {
      placement: this.imagePlacement,
      hideOnMobile: this.imageHideOnMobile,
      placementPercentage: this.placementSlider,
      imageFormat: this.imageFormat,
    };
  }

  createColorsAPIModel(): IAPICustomColors {
    return {
      primary: this.mainColors[0].createAPIModel(),
      notSelected: this.mainColors[1].createAPIModel(),
      tabs: defaultColor,
      recommended: defaultColor,
      innerBackground: this.backgroundColors[0].createAPIModel(),
      outerBackground: defaultColor,
      pageBackground: this.backgroundColors[1].createAPIModel(),
      innerBorder: this.borderColors[0].createAPIModel(),
      fontText: this.fontColors[0].createAPIModel(),
      fontActiveButton: this.fontColors[1].createAPIModel(),
      fontInactiveButton: this.fontColors[2].createAPIModel(),
      outerBorder: defaultColor,
      selectedBar: defaultColor,
      unSelectedBar: defaultColor,
      buttonCustomCaption: "",
      buttonStyleType: CheckOutButtonStyle.SquareRoundCorner,
      payButtonType: CheckOutPayButtonCaption.Pay,
      defaultSegment: Object.keys(
        CheckOutDefaultSegmentType
      )[0] as keyof typeof CheckOutDefaultSegmentType,
    };
  }

  static deserializeAPIModel = (
    mediaPlacement: IAPIMediaPlacementModel,
    media: IAPILinkedMediaModel,
    colorsData: IAPICustomColors
  ) => {
    if (!mediaPlacement) {
      return new DonationCheckoutModel();
    }
    return new DonationCheckoutModel({
      imagePlacement: mediaPlacement.placement,
      imageFormat: mediaPlacement.imageFormat,
      imageHideOnMobile: mediaPlacement.hideOnMobile,
      placementSlider: mediaPlacement.placementPercentage,
      media:
        media.fullURL !== "" ? [MediaModel.deserializeAPIModel(media)] : [],
      mainColors: [
        new ColorPickerElement({ color: colorsData.primary, name: "Primary" }),
        new ColorPickerElement({
          color: colorsData.notSelected,
          name: "Secondary",
        }),
      ],
      backgroundColors: [
        new ColorPickerElement({
          color: colorsData.innerBackground,
          name: "Inner",
        }),
        new ColorPickerElement({
          color: colorsData.pageBackground,
          name: "Page",
        }),
      ],
      borderColors: [
        new ColorPickerElement({
          color: colorsData.innerBorder,
          name: "Inner",
        }),
      ],
      fontColors: [
        new ColorPickerElement({
          color: colorsData.fontText,
          name: "Default",
        }),
        new ColorPickerElement({
          color: colorsData.fontActiveButton,
          name: "Active Button",
        }),
        new ColorPickerElement({
          color: colorsData.fontInactiveButton,
          name: "Muted Button",
        }),
      ],
    });
  };
}

const defaultBasicColors = {
  primary: defaultColor,
  tabs: defaultColor,
  recommended: defaultColor,
  pageBackground: defaultColor,
};

export class BasicColors {
  colorPickers: ColorPickerElement[];
  buttonStyleType: keyof typeof CheckOutButtonStyle;
  buttonCustomCaption: string;
  payButtonType: keyof typeof CheckOutPayButtonCaption;
  defaultSegment: keyof typeof CheckOutDefaultSegmentType;
  errors: InputErrorModel<BasicColors>[];

  constructor(data?: Partial<BasicColors>) {
    this.colorPickers = (data?.colorPickers ?? basicColorPickerElements).map(
      (el) => new ColorPickerElement(el)
    );
    this.buttonStyleType =
      (data?.buttonStyleType as keyof typeof CheckOutButtonStyle) ||
      CheckOutButtonStyle.SquareRoundCorner;
    this.buttonCustomCaption = data?.buttonCustomCaption || "";
    this.payButtonType =
      (data?.payButtonType as keyof typeof CheckOutPayButtonCaption) ||
      CheckOutPayButtonCaption.Pay;
    this.defaultSegment =
      (data?.defaultSegment as keyof typeof CheckOutDefaultSegmentType) ||
      (Object.keys(
        CheckOutDefaultSegmentType
      )[0] as keyof typeof CheckOutDefaultSegmentType);
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<BasicColors>(e);
        })) ||
      [];
  }

  getBasicColors() {
    const findColor = (pickers: ColorPickerElement[], name: string) =>
      pickers.find((el) => el.name === name)?.createAPIModel() ?? defaultColor;

    return {
      primary: findColor(this.colorPickers, "Primary"),
      notSelected: findColor(this.colorPickers, "NotSelected"),
      tabs: findColor(this.colorPickers, "Tabs"),
      recommended: findColor(this.colorPickers, "Recommended"),
      pageBackground: findColor(this.colorPickers, "Page Background"),
      buttonStyleType: this.buttonStyleType,
      buttonCustomCaption: this.buttonCustomCaption,
      payButtonType: this.payButtonType,
      defaultSegment: this.defaultSegment,
    } as IAPIBasicColors;
  }

  createAPIModel(): IAPIBasicColors {
    return {
      ...this.getBasicColors(),
      buttonStyleType: this.buttonStyleType,
      buttonCustomCaption: this.buttonCustomCaption,
      payButtonType: this.payButtonType,
      defaultSegment: this.defaultSegment,
    };
  }

  hasError(field: keyof BasicColors): boolean {
    return this.errors.some((e) => e.field === field);
  }

  static deserializeAPIModel(data: Partial<IAPIBasicColors>): BasicColors {
    if (!data) {
      return new BasicColors();
    }
    const colors = data ? { ...defaultBasicColors, ...data } : defaultColors;

    const createColorPickers = (names: string[], colors: IColor[]) =>
      names.map(
        (name, i) => new ColorPickerElement({ name, color: colors[i] })
      );

    const mappedData: Partial<BasicColors> = {
      colorPickers: createColorPickers(
        ["Primary", "Tabs", "Recommended", "Page Background"],
        [colors.primary, colors.tabs, colors.recommended, colors.pageBackground]
      ),
      buttonCustomCaption: data.buttonCustomCaption,
      buttonStyleType: data.buttonStyleType as keyof typeof CheckOutButtonStyle,
      payButtonType:
        data.payButtonType as keyof typeof CheckOutPayButtonCaption,
      defaultSegment:
        data.defaultSegment as keyof typeof CheckOutDefaultSegmentType,
    };
    return new BasicColors(mappedData);
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<BasicColors>[] = [];
    if (this.payButtonType === CheckOutPayButtonCaption.Custom) {
      CoreInputModelUtil.validateTextField(
        this.buttonCustomCaption,
        "buttonCustomCaption",
        newErrors
      );
    }
    CoreInputModelUtil.validateTextField(
      this.payButtonType,
      "payButtonType",
      newErrors
    );
    CoreInputModelUtil.validateTextField(
      this.defaultSegment,
      "defaultSegment",
      newErrors
    );
    this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;
    return this.errors.length === 0;
  }
}
